import React, {useEffect, useState} from 'react';
import {Autocomplete, Box, CircularProgress, Switch, TextField, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {useTranslation} from 'react-i18next';
import {store} from '@/service/store';
import WarningAlert from '@/components/common/WarningAlert';
import {useSignalEffect} from '@preact/signals-react';
import {useSignals} from "@preact/signals-react/runtime";
import {licenseNumberPattern, vinPattern} from "@/utils/patterns.ts";

interface Car {
  uuid: string;
  vin: string;
  make?: string | null;
  model?: string | null;
  currentInstallation: null | any;
}

const VehicleIdentificationStep: React.FC = () => {
  useSignals();

  const { t } = useTranslation();
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  const [licenseNumberError, setLicenseNumberError] = useState<string | null>(null);
  const [vinError, setVinError] = useState<string | null>(null);

  const [licenseNumber, setLicenseNumber] = useState<string>(store.installationAssistant.licenseNumber.value || '');
  const [carNotRegistered, setCarNotRegistered] = useState<boolean>(false);
  const [vehicleMileage, setVehicleMileage] = useState<string>(store.installationAssistant.vehicleMileage.value || '');
  const [vehicleVIN, setVehicleVIN] = useState<string>(store.installationAssistant.vehicleVIN.value || '');

  const customerNumber = store.installationAssistant.customerNumber.value;
  const customer = store.customers.get(customerNumber);
  const cars = customer.cars.data.value;
  const loading = customer.cars.isLoading.value;

  useEffect(() => {
    customer.cars.load();
  }, []);

  useSignalEffect(() => {
    setLicenseNumber(store.installationAssistant.licenseNumber.value);
    setVehicleMileage(store.installationAssistant.vehicleMileage.value);
    setVehicleVIN(store.installationAssistant.vehicleVIN.value);
    setCarNotRegistered(store.installationAssistant.carNotRegistered.value);
  });

  const handleCarSelection = (_event: any, car: Car | null) => {
    setSelectedCar(car);
    if (car) {
      const vin = car.vin;
      setVehicleVIN(vin);
      store.installationAssistant.vehicleVIN.value = vin;
      store.installationAssistant.vehicleExists.value = true;
      setVinError(null);
    } else {
      store.installationAssistant.vehicleExists.value = false;
    }
  };

  const handleVinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase();

    if (value.length > 17) {
      value = value.slice(0, 17);
    }

    setVehicleVIN(value);
    store.installationAssistant.vehicleVIN.value = value;

    if (!value) {
      setVinError(t('assistant.vehicleIdentificationStep.vinRequired'));
    } else if (value.length !== 17 || !vinPattern.test(value)) {
      setVinError(t('assistant.vehicleIdentificationStep.invalidVin'));
    } else {
      setVinError(null);
      const existingCar = cars.find(car => car.vin === value);
      if (existingCar) {
        setSelectedCar(existingCar);
        store.installationAssistant.vehicleExists.value = true;
      } else {
        setSelectedCar(null);
        store.installationAssistant.vehicleExists.value = false;
      }
    }
  };

  const handleLicenseNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();

    setLicenseNumber(value);
    store.installationAssistant.licenseNumber.value = value;

    if (!value) {
      setLicenseNumberError(t('assistant.vehicleIdentificationStep.licensePlateRequired'));

      return;
    }

    if (!licenseNumberPattern.test(value)) {
      setLicenseNumberError(t('assistant.vehicleIdentificationStep.invalidLicenseNumber'));

      return;
    }

    setLicenseNumberError(null);
  };

  const handleCarNotRegisteredSwitch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setCarNotRegistered(checked);
    store.installationAssistant.carNotRegistered.value = checked;

    if (checked) {
      setLicenseNumber('');
      store.installationAssistant.licenseNumber.value = '';
      setLicenseNumberError(null);
    }
  }

  const handleMileageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setVehicleMileage(value);
    store.installationAssistant.vehicleMileage.value = value;
  };

  const hasCurrentInstallation = typeof selectedCar === 'string' ? false : selectedCar?.currentInstallation !== null;

  useSignalEffect(() => {
    if (store.installationAssistant.vehicleVIN.value) {
      const car = cars.find(c => c.vin === store.installationAssistant.vehicleVIN.value);
      setSelectedCar(car || null);
      store.installationAssistant.vehicleExists.value = !!car;
    }
  });

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 'medium' }}>
        {t('assistant.vehicleIdentificationStep.title')}
      </Typography>

      <Typography variant="body2" sx={{ mb: 3 }}>
        {t('assistant.vehicleIdentificationStep.description')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={12}>
          <Autocomplete
            freeSolo
            options={cars}
            loading={loading}
            value={selectedCar}
            onChange={handleCarSelection}
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option;
              }
              return option.vin;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('assistant.vehicleIdentificationStep.vin')}
                value={vehicleVIN}
                onChange={handleVinChange}
                error={!!vinError}
                helperText={vinError}
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    maxLength: 17
                  },
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps?.endAdornment}
                      </>
                    )
                  }
                }}
              />
            )}
          />
          {selectedCar && hasCurrentInstallation && (
            <WarningAlert
              message={t('assistant.vehicleIdentificationStep.hasInstallation')}
              className="mt-2"
            />
          )}
        </Grid>

        <Grid size={12}>
          <Box sx={{
            display: 'flex',
            flexDirection: {xs: 'column', md: 'row'},
            alignItems: {xs: 'stretch', md: 'center'},
            gap: 2
          }}>
            <TextField
              fullWidth
              label={t('assistant.vehicleIdentificationStep.licenseNumber')}
              value={licenseNumber}
              onChange={handleLicenseNumberChange}
              error={!!licenseNumberError}
              helperText={licenseNumberError}
              disabled={carNotRegistered}
            />
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 120,
              gap: 1,
              justifyContent: {xs: 'flex-start', md: 'center'}
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <Switch
                  checked={carNotRegistered}
                  onChange={handleCarNotRegisteredSwitch}
                  size="medium"
                />
                {t('assistant.vehicleIdentificationStep.carNotRegistered')}
              </label>
            </Box>
          </Box>
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label={t('assistant.vehicleIdentificationStep.mileage')}
            value={vehicleMileage}
            onChange={handleMileageChange}
            variant="outlined"
            type="text"
            slotProps={{
              input: {
                endAdornment: <Typography variant="body2" color="text.secondary">km</Typography>
              }
            }}
            helperText={t('assistant.vehicleIdentificationStep.optional')}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default VehicleIdentificationStep;
